import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Camera, Loader2, X } from 'lucide-react';
import { posAPI, purchasesAPI } from '@/lib/api';
import CameraScanner from '@/components/CameraScanner';
import { toast } from 'sonner';

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

export default function FilterControls({
  selectedProduct,
  onProductSelect,
  onProductClear,
  categoryFilter,
  setCategoryFilter,
  categoryOptions,
}) {
  const [searchQ, setSearchQ]               = useState('');
  const [allProducts, setAllProducts]       = useState([]);
  const [searchResults, setSearchResults]   = useState([]);
  const [showDropdown, setShowDropdown]     = useState(false);
  const [selectedIdx, setSelectedIdx]       = useState(-1);
  const [scannerOpen, setScannerOpen]       = useState(false);
  const [barcodeLoading, setBarcodeLoading] = useState(false);

  const searchRef        = useRef(null);
  const dropdownRef      = useRef(null);
  const searchQueryRef   = useRef('');
  const catalogLoadedRef = useRef(false);

  // Sync input with selectedProduct
  useEffect(() => {
    if (!selectedProduct) {
      setSearchQ('');
      searchQueryRef.current = '';
    } else {
      const name = selectedProduct.name || selectedProduct.product_name || '';
      setSearchQ(name);
      searchQueryRef.current = name;
    }
  }, [selectedProduct]);

  // Lazy catalog load on first focus
  const loadCatalog = useCallback(async () => {
    if (catalogLoadedRef.current) return;
    catalogLoadedRef.current = true;
    try {
      const r = await posAPI.getProducts({ limit: 10000 });
      setAllProducts(r.data?.items ?? []);
    } catch {
      catalogLoadedRef.current = false;
    }
  }, []);

  // 6-tier scoring — same as POS / SalesHistory
  const doSearch = useCallback((q) => {
    if (!q.trim()) { setSearchResults([]); setShowDropdown(false); return; }
    const lower = q.toLowerCase();

    const score = (p) => {
      const bc   = String(p.barcode || '').toLowerCase();
      const sk   = String(p.sku || '').toLowerCase();
      const name = (p.name || p.product_name || '').toLowerCase();
      if (bc === lower || sk === lower)                 return 0;
      if (bc.startsWith(lower) || sk.startsWith(lower)) return 1;
      if (name.startsWith(lower))                       return 2;
      if (bc.includes(lower) || sk.includes(lower))     return 3;
      if (name.includes(lower))                         return 4;
      return 5;
    };

    const results = allProducts
      .filter(p => {
        const name = (p.name || p.product_name || '').toLowerCase();
        return (
          name.includes(lower) ||
          String(p.barcode || '').toLowerCase().includes(lower) ||
          String(p.sku || '').toLowerCase().includes(lower) ||
          (p.category || '').toLowerCase().includes(lower) ||
          (p.brand || '').toLowerCase().includes(lower)
        );
      })
      .sort((a, b) => score(a) - score(b))
      .slice(0, 10);

    setSearchResults(results);
    setSelectedIdx(results.length > 0 ? 0 : -1);
    setShowDropdown(true);
  }, [allProducts]);

  // Re-run after catalog loads (fixes first-focus race)
  useEffect(() => {
    if (allProducts.length > 0 && searchQueryRef.current.trim())
      doSearch(searchQueryRef.current);
  }, [allProducts, doSearch]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (!dropdownRef.current) return;
    dropdownRef.current.querySelectorAll('button')[selectedIdx]?.scrollIntoView({ block: 'nearest' });
  }, [selectedIdx]);

  const handleSelect = useCallback((product) => {
    const name = product.name || product.product_name || '';
    setSearchQ(name);
    searchQueryRef.current = name;
    setShowDropdown(false);
    setSearchResults([]);
    onProductSelect(product);
  }, [onProductSelect]);

  const handleCameraDetected = useCallback(async (barcode) => {
    setScannerOpen(false);
    if (!barcode?.trim()) return;
    setBarcodeLoading(true);
    try {
      const r = await purchasesAPI.lookupBarcode(barcode.trim());
      handleSelect(r.data);
    } catch {
      toast.error('No product found for this barcode');
    } finally {
      setBarcodeLoading(false);
    }
  }, [handleSelect]);

  const handleChange = useCallback((e) => {
    const val = e.target.value;
    setSearchQ(val);
    searchQueryRef.current = val;
    if (!val) {
      setSearchResults([]);
      setShowDropdown(false);
      onProductClear();
    } else {
      doSearch(val);
    }
  }, [doSearch, onProductClear]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { setShowDropdown(false); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showDropdown && searchResults.length > 0) { setShowDropdown(true); setSelectedIdx(0); }
      else setSelectedIdx(i => Math.min(i + 1, searchResults.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showDropdown && selectedIdx >= 0 && selectedIdx < searchResults.length) {
        handleSelect(searchResults[selectedIdx]);
      } else {
        setShowDropdown(false);
      }
    }
  }, [showDropdown, selectedIdx, searchResults, handleSelect]);

  const handleClear = useCallback(() => {
    setSearchQ('');
    searchQueryRef.current = '';
    setSearchResults([]);
    setShowDropdown(false);
    onProductClear();
    searchRef.current?.focus();
  }, [onProductClear]);

  return (
    <>
      <div className="flex items-center gap-2 ml-auto flex-wrap">
        {/* Product search with dropdown */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none z-10" />
          <input
            ref={searchRef}
            type="text"
            value={searchQ}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => { loadCatalog(); if (searchQ.trim()) setShowDropdown(true); }}
            onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
            placeholder="Search product or barcode…"
            className={`pl-8 pr-7 py-1.5 text-sm border rounded-lg bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 w-48 ${
              selectedProduct ? 'border-primary' : 'border-border'
            }`}
          />
          {searchQ && (
            <button
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}

          {/* Dropdown */}
          {showDropdown && searchResults.length > 0 && (
            <div
              ref={dropdownRef}
              className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto min-w-[280px]"
            >
              {searchResults.map((p, i) => (
                <button
                  key={p.catalog_id}
                  onMouseDown={() => handleSelect(p)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-muted/60 text-left ${i === selectedIdx ? 'bg-primary/10' : ''}`}
                >
                  <div className="min-w-0 mr-3">
                    <div className="font-medium truncate">{p.name || p.product_name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {[p.brand, p.category, p.pack_size].filter(Boolean).join(' · ')}
                      {p.barcode && <span className="ml-2 font-mono">{p.barcode}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold text-emerald-700 text-xs">{fmt(p.mrp ?? 0)}</div>
                    <div className="text-xs text-muted-foreground">Stock: {p.stock ?? 0}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Camera scan — always visible */}
        <button
          onClick={() => { loadCatalog(); setScannerOpen(true); }}
          disabled={barcodeLoading}
          title="Scan barcode"
          className="p-1.5 rounded-lg border border-border bg-card text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
        >
          {barcodeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
        </button>

        {/* Category dropdown */}
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="text-sm border border-border rounded-lg bg-card text-foreground px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/20 max-w-[160px]"
        >
          <option value="All">All Categories</option>
          {categoryOptions.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {scannerOpen && (
        <CameraScanner onDetected={handleCameraDetected} onClose={() => setScannerOpen(false)} />
      )}
    </>
  );
}
